/**
 * MarketBrewer LLC - Mega Footer
 *
 * Full-site footer navigation organized as DC Metro "lines":
 * - Metro stripe signature at top (6-color WMATA gradient)
 * - Brand column: logo, availability badge, tagline, contact, socials
 * - Five link groups, each keyed to a Metro line color:
 *   Web & SEO (blue) · Marketing & Media (green) · Company (yellow) ·
 *   Work With Me (red) · Industries (silver)
 * - Bottom bar: copyright + Privacy / Terms / Accessibility
 * - Full bilingual support (EN/ES)
 */

import React from "react";
import { Link } from "react-router-dom";
import Container from "@/components/Shared/Container";
import TrackedLink from "@/components/Shared/TrackedLink";
import { COMPANY, CONTACT, JORGE } from "@/constants";

interface FooterProps {
  language: "en" | "es";
}

type Accent = "blue" | "green" | "yellow" | "red" | "silver";

interface FooterLink {
  to: string;
  label: string;
  /** Orange conversion accent (e.g. the 15-min call link). */
  highlight?: boolean;
}

interface FooterSection {
  title: string;
  accent: Accent;
  links: FooterLink[];
}

interface SocialLink {
  name: string;
  href: string;
  icon: React.ReactNode;
}

// Static class maps so Tailwind can see every class at build time.
const accentDotClass: Record<Accent, string> = {
  blue: "bg-metro-blue-500",
  green: "bg-metro-green-500",
  yellow: "bg-metro-yellow-400",
  red: "bg-metro-red-500",
  silver: "bg-metro-silver-400",
};

const accentHoverDotClass: Record<Accent, string> = {
  blue: "group-hover:bg-metro-blue-500",
  green: "group-hover:bg-metro-green-500",
  yellow: "group-hover:bg-metro-yellow-400",
  red: "group-hover:bg-metro-red-500",
  silver: "group-hover:bg-metro-silver-400",
};

const Footer: React.FC<FooterProps> = ({ language }) => {
  const currentYear = new Date().getFullYear();

  const content =
    language === "en"
      ? {
          srTitle: "Footer navigation",
          tagline: `One developer. One studio. Custom websites, SEO, Google Maps, and photography for businesses across the ${COMPANY.SERVICE_AREA}.`,
          availability: "Taking on a few new clients each quarter",
          location: COMPANY.SERVICE_AREA,
          area: `${COMPANY.LOCATION.CITY}, ${COMPANY.LOCATION.STATE}`,
          email: CONTACT.EMAIL,
          phone: CONTACT.PHONE.GOOGLE_VOICE_FORMATTED,
          rights: "All rights reserved.",
          privacy: "Privacy Policy",
          terms: "Terms of Service",
          accessibility: "Accessibility",
          followUs: "Follow Us",
          home: "/",
          sections: [
            {
              title: "Web & SEO",
              accent: "blue",
              links: [
                { to: "/services/website-development", label: "Website Development" },
                { to: "/services/local-seo", label: "SEO" },
                { to: "/services/technical-seo", label: "Technical SEO" },
                { to: "/services/google-business-profile", label: "Google Maps Optimization" },
                { to: "/services/law-firm-websites", label: "Law Firm Websites" },
                { to: "/services/ecommerce-websites", label: "Ecommerce Websites" },
              ],
            },
            {
              title: "Marketing & Media",
              accent: "green",
              links: [
                { to: "/services/digital-marketing", label: "Digital Marketing" },
                { to: "/services/growth-marketing", label: "Growth Marketing" },
                { to: "/services/social-media-management", label: "Social Media Management" },
                { to: "/services/photography", label: "Photography" },
                { to: "/services/photography/food", label: "Food Photography" },
                { to: "/services/photography/model", label: "Model Photography" },
                { to: "/services/live-stream-production", label: "Live Stream Production" },
              ],
            },
            {
              title: "Company",
              accent: "yellow",
              links: [
                { to: "/about", label: "About" },
                { to: "/portfolio", label: "Portfolio" },
                { to: "/contact", label: "Contact" },
                { to: "/book", label: "Set up a 15-min call", highlight: true },
                { to: "/live", label: "Live" },
                { to: "/links", label: "Links Hub" },
                { to: "/google-check", label: "Free Google Check" },
                { to: "/website-audit", label: "Free Website Audit" },
                { to: "/hire-me", label: "Hire Me" },
                { to: "/directory", label: "Site Directory" },
              ],
            },
            {
              title: "Work With Me",
              accent: "red",
              links: [
                { to: "/digital-marketing-specialist", label: "Digital Marketing Specialist" },
                { to: "/cto", label: "Chief Technology Officer (CTO)" },
                { to: "/services/growth-marketing", label: "Growth Specialist" },
                { to: "/business-partners", label: "Business Partners" },
              ],
            },
            {
              title: "Industries",
              accent: "silver",
              links: [
                { to: "/industries", label: "All Industries" },
                { to: "/industries/restaurants", label: "Restaurants" },
                { to: "/industries/attorneys", label: "Law Firms" },
                { to: "/industries/ecommerce", label: "Ecommerce" },
                { to: "/industries/fashion-brands", label: "Fashion Brands" },
                { to: "/industries/salons", label: "Salons & Beauty" },
                { to: "/industries/child-care", label: "Child Care" },
              ],
            },
          ] as FooterSection[],
        }
      : {
          srTitle: "Navegación del pie de página",
          tagline: `Un solo desarrollador. Un solo estudio. Sitios web a la medida, SEO, Google Maps y fotografía para negocios en el ${COMPANY.SERVICE_AREA}.`,
          availability: "Acepto pocos clientes nuevos cada trimestre",
          location: COMPANY.SERVICE_AREA,
          area: `${COMPANY.LOCATION.CITY}, ${COMPANY.LOCATION.STATE}`,
          email: CONTACT.EMAIL,
          phone: CONTACT.PHONE.GOOGLE_VOICE_FORMATTED,
          rights: "Todos los derechos reservados.",
          privacy: "Política de Privacidad",
          terms: "Términos de Servicio",
          accessibility: "Accesibilidad",
          followUs: "Síguenos",
          home: "/es",
          sections: [
            {
              title: "Web y SEO",
              accent: "blue",
              links: [
                { to: "/es/servicios/desarrollo-web", label: "Desarrollo Web" },
                { to: "/es/servicios/seo-local", label: "SEO" },
                { to: "/es/servicios/seo-tecnico", label: "SEO Técnico" },
                { to: "/es/servicios/google-business-profile", label: "Optimización de Google Maps" },
                { to: "/es/servicios/sitios-web-bufetes", label: "Sitios Web para Bufetes" },
                { to: "/es/servicios/sitios-web-ecommerce", label: "Sitios Web Ecommerce" },
              ],
            },
            {
              title: "Marketing y Medios",
              accent: "green",
              links: [
                { to: "/es/servicios/marketing-digital", label: "Marketing Digital" },
                { to: "/es/servicios/marketing-de-crecimiento", label: "Marketing de Crecimiento" },
                { to: "/es/servicios/gestion-redes-sociales", label: "Gestión de Redes Sociales" },
                { to: "/es/servicios/fotografia", label: "Fotografía" },
                { to: "/es/servicios/fotografia/comida", label: "Fotografía de Comida" },
                { to: "/es/servicios/fotografia/modelo", label: "Fotografía de Modelos" },
                { to: "/es/servicios/transmisiones-en-vivo", label: "Transmisiones en Vivo" },
              ],
            },
            {
              title: "Empresa",
              accent: "yellow",
              links: [
                { to: "/es/sobre-nosotros", label: "Sobre Nosotros" },
                { to: "/es/portafolio", label: "Portafolio" },
                { to: "/es/contacto", label: "Contacto" },
                { to: "/es/agendar", label: "Agendar una llamada", highlight: true },
                { to: "/es/en-vivo", label: "En Vivo" },
                { to: "/es/enlaces", label: "Centro de Enlaces" },
                { to: "/es/revision-google", label: "Revisión Gratuita de Google" },
                { to: "/es/auditoria-web", label: "Auditoría Web Gratuita" },
                { to: "/es/contratame", label: "Contrátame" },
                { to: "/es/directorio", label: "Directorio del Sitio" },
              ],
            },
            {
              title: "Trabaje Conmigo",
              accent: "red",
              links: [
                { to: "/es/especialista-en-marketing-digital", label: "Especialista en Marketing Digital" },
                { to: "/es/cto", label: "Director de Tecnología (CTO)" },
                { to: "/es/servicios/marketing-de-crecimiento", label: "Especialista en Crecimiento" },
                { to: "/es/socios-de-negocio", label: "Socios de Negocio" },
              ],
            },
            {
              title: "Industrias",
              accent: "silver",
              links: [
                { to: "/es/industrias", label: "Todas las Industrias" },
                { to: "/es/industrias/restaurantes", label: "Restaurantes" },
                { to: "/es/industrias/abogados", label: "Bufetes de Abogados" },
                { to: "/es/industrias/ecommerce", label: "Ecommerce" },
                { to: "/es/industrias/marcas-de-moda", label: "Marcas de Moda" },
                { to: "/es/industrias/salones", label: "Salones y Belleza" },
                { to: "/es/industrias/cuidado-infantil", label: "Cuidado Infantil" },
              ],
            },
          ] as FooterSection[],
        };

  // Social media links
  const socialLinks: SocialLink[] = [
    {
      name: "Kick",
      href: JORGE.SOCIAL.KICK,
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M1.333 0h8v5.333H12V2.667h2.667V0h8v8H20v2.667h-2.667v2.666H20V16h2.667v8h-8v-2.667H12v-2.666H9.333V24h-8Z" />
        </svg>
      ),
    },
    {
      name: "YouTube",
      href: JORGE.SOCIAL.YOUTUBE,
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      ),
    },
    {
      name: "TikTok",
      href: JORGE.SOCIAL.TIKTOK,
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
        </svg>
      ),
    },
    {
      name: "LinkedIn",
      href: JORGE.SOCIAL.LINKEDIN,
      icon: (
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
    },
    {
      name: "Facebook",
      href: JORGE.SOCIAL.FACEBOOK,
      icon: (
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    {
      name: "Instagram",
      href: JORGE.SOCIAL.INSTAGRAM,
      icon: (
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
  ];

  return (
    <footer className="bg-dark-950 text-dark-300">
      {/* Metro stripe signature at top */}
      <div className="metro-stripe" aria-hidden="true" />

      {/* Main Footer Content */}
      <Container size="xl" className="pt-14 pb-12 lg:pt-20 lg:pb-16">
        <h2 className="sr-only">{content.srTitle}</h2>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-4 lg:pr-10">
            <Link
              to={content.home}
              className="inline-flex flex-col mb-4 group rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-metro-orange-500"
            >
              <span className="text-2xl font-bold tracking-tight transition-colors text-dark-50 group-hover:text-metro-orange-500">
                Market
                <span className="transition-colors text-metro-orange-500 group-hover:text-dark-50">
                  Brewer
                </span>
              </span>
              <span className="text-[10px] text-dark-400 tracking-widest uppercase">
                LLC
              </span>
            </Link>

            {/* Availability badge */}
            <p className="flex items-center gap-2.5 mb-4 text-sm text-dark-300">
              <span
                className="flex-shrink-0 w-2 h-2 rounded-full bg-metro-green-500 animate-pulse"
                aria-hidden="true"
              />
              {content.availability}
            </p>

            <p className="max-w-xs mb-8 text-sm leading-relaxed text-dark-400">
              {content.tagline}
            </p>

            {/* Contact rows */}
            <address className="mb-8 space-y-3 text-sm not-italic">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 p-2 rounded-lg bg-dark-800 text-metro-green-500">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-dark-300">{content.location}</p>
                  <p className="text-dark-400">{content.area}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 p-2 rounded-lg bg-dark-800 text-metro-orange-500">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>
                <TrackedLink
                  href={`tel:+1${CONTACT.PHONE.GOOGLE_VOICE}`}
                  type="phone"
                  className="transition-colors text-dark-300 hover:text-metro-orange-500"
                >
                  {content.phone}
                </TrackedLink>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 p-2 rounded-lg bg-dark-800 text-metro-orange-500">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <TrackedLink
                  href={`mailto:${content.email}`}
                  type="email"
                  className="transition-colors text-dark-300 hover:text-metro-orange-500"
                >
                  {content.email}
                </TrackedLink>
              </div>
            </address>

            {/* Social Links */}
            <div>
              <p className="mb-3 text-xs tracking-wider uppercase text-dark-400">
                {content.followUs}
              </p>
              <div className="flex items-center gap-3">
                {socialLinks.map((social) => (
                  <TrackedLink
                    key={social.name}
                    href={social.href}
                    type="social"
                    platform={social.name}
                    newTab
                    className="p-2 transition-colors rounded-lg bg-dark-800 text-dark-400 hover:bg-dark-700 hover:text-metro-orange-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-metro-orange-500"
                    aria-label={social.name}
                  >
                    {social.icon}
                  </TrackedLink>
                ))}
              </div>
            </div>
          </div>

          {/* Link Matrix — one nav per Metro line */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10 lg:col-span-8 lg:gap-x-8">
            {content.sections.slice(0, 3).map((section) => (
              <nav key={section.title} aria-label={section.title}>
                <div className="flex items-center gap-2 pb-2 mb-4 border-b border-dark-800">
                  <span
                    className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${accentDotClass[section.accent]}`}
                    aria-hidden="true"
                  />
                  <h3 className="text-xs font-semibold tracking-[0.16em] uppercase text-dark-100">
                    {section.title}
                  </h3>
                </div>
                <ul className="space-y-2.5">
                  {section.links.map((link) => (
                    <li key={`${link.to}-${link.label}`}>
                      <Link
                        to={link.to}
                        className={`group inline-flex items-center gap-2 py-0.5 text-sm transition-colors rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-metro-orange-500 ${
                          link.highlight
                            ? "font-medium text-metro-orange-400 hover:text-metro-orange-300"
                            : "text-dark-400 hover:text-dark-50"
                        }`}
                      >
                        <span
                          className={`w-1 h-1 rounded-full flex-shrink-0 transition-colors ${
                            link.highlight
                              ? "bg-metro-orange-500"
                              : `bg-dark-700 ${accentHoverDotClass[section.accent]}`
                          }`}
                          aria-hidden="true"
                        />
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            ))}

            {/* Fourth column: Work With Me + Industries stacked */}
            <div className="space-y-10">
              {content.sections.slice(3).map((section) => (
                <nav key={section.title} aria-label={section.title}>
                  <div className="flex items-center gap-2 pb-2 mb-4 border-b border-dark-800">
                    <span
                      className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${accentDotClass[section.accent]}`}
                      aria-hidden="true"
                    />
                    <h3 className="text-xs font-semibold tracking-[0.16em] uppercase text-dark-100">
                      {section.title}
                    </h3>
                  </div>
                  <ul className="space-y-2.5">
                    {section.links.map((link) => (
                      <li key={`${link.to}-${link.label}`}>
                        <Link
                          to={link.to}
                          className="group inline-flex items-center gap-2 py-0.5 text-sm transition-colors rounded-sm text-dark-400 hover:text-dark-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-metro-orange-500"
                        >
                          <span
                            className={`w-1 h-1 rounded-full flex-shrink-0 transition-colors bg-dark-700 ${accentHoverDotClass[section.accent]}`}
                            aria-hidden="true"
                          />
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
              ))}
            </div>
          </div>
        </div>
      </Container>

      {/* Bottom Bar */}
      <div className="border-t border-dark-800">
        <Container size="xl" className="py-6">
          <div className="flex flex-col items-center justify-between gap-4 text-sm md:flex-row">
            <p className="font-mono text-xs text-dark-400">
              © {currentYear} {COMPANY.NAME}. {content.rights}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              <Link
                to={
                  language === "en"
                    ? "/privacy-policy"
                    : "/es/politica-de-privacidad"
                }
                className="transition-colors text-dark-400 hover:text-dark-200"
              >
                {content.privacy}
              </Link>
              <Link
                to={
                  language === "en"
                    ? "/terms-of-service"
                    : "/es/terminos-de-servicio"
                }
                className="transition-colors text-dark-400 hover:text-dark-200"
              >
                {content.terms}
              </Link>
              <Link
                to={
                  language === "en" ? "/accessibility" : "/es/accesibilidad"
                }
                className="transition-colors text-dark-400 hover:text-dark-200"
              >
                {content.accessibility}
              </Link>
            </div>
          </div>
        </Container>
      </div>
    </footer>
  );
};

export default Footer;
